import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TRANSLATIONS } from '../data/translations';
import { LANGUAGES } from '../config/seo';
import { trackEvent } from '../lib/analytics';

// Map from component keys to actual translation keys
const KEY_ALIASES = {
  card_1_number: '11', card_1_label: 'card1_label', card_1_desc: 'card1_desc',
  card_2_number: '1200', card_2_label: 'card2_label', card_2_desc: 'card2_desc',
  card_3_number: '∞', card_3_label: 'card3_label', card_3_desc: 'card3_desc',
  card_full: 'card4_desc', rimmo_note_pre: 'rimmo_text', rimmo_wa_label: 'rimmo_wa_btn',
  units_project_label: 'einheiten_label', units_headline: 'einheiten_headline',
  th_unit: 'table_col_unit', th_floor: 'table_col_floor', th_rooms: 'table_col_rooms',
  th_area: 'table_col_area', th_price: 'table_col_price', units_loading: 'table_loading',
  cs_label: 'coming_label', cs_headline: 'coming_headline', cs_text: 'coming_text',
  cookie_btn_essential: 'cookie_essential', cookie_btn_all: 'cookie_accept',
};

// Extra translations not in the original file
const EXTRA = {
  de: { nav_projekt:'Projekt', nav_impressionen:'Impressionen', nav_lage:'Lage', nav_qualitaet:'Qualität', nav_einheiten:'Einheiten', nav_kontakt:'Kontakt', form_success:'Vielen Dank! Ihre Anfrage wurde erfolgreich übermittelt.', form_error:'Es gab ein Problem. Bitte versuchen Sie es erneut.', filter_rooms:'Zimmer', filter_floor:'Etage', filter_all:'Alle', filter_sort:'Sortierung', filter_sort_unit:'Einheit', filter_sort_price:'Preis', filter_sort_area:'Fläche' },
  it: { nav_projekt:'Progetto', nav_impressionen:'Galleria', nav_lage:'Posizione', nav_qualitaet:'Qualità', nav_einheiten:'Unità', nav_kontakt:'Contatto', form_success:'Grazie! La sua richiesta è stata inviata con successo.', form_error:'Si è verificato un errore. Riprovi via WhatsApp.', filter_rooms:'Stanze', filter_floor:'Piano', filter_all:'Tutti', filter_sort:'Ordina', filter_sort_unit:'Unità', filter_sort_price:'Prezzo', filter_sort_area:'Superficie' },
  en: { nav_projekt:'Project', nav_impressionen:'Gallery', nav_lage:'Location', nav_qualitaet:'Quality', nav_einheiten:'Units', nav_kontakt:'Contact', form_success:'Thank you! Your inquiry has been submitted.', form_error:'Something went wrong. Please try again.', filter_rooms:'Rooms', filter_floor:'Floor', filter_all:'All', filter_sort:'Sort by', filter_sort_unit:'Unit', filter_sort_price:'Price', filter_sort_area:'Area' },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathLanguage = location.pathname.split('/').filter(Boolean)[0];
  const [lang, setLangState] = useState(() => LANGUAGES.includes(pathLanguage) ? pathLanguage : localStorage.getItem('tres_lang') || 'de');

  const setLang = useCallback((l) => {
    if (!LANGUAGES.includes(l)) return;
    setLangState(l);
    localStorage.setItem('tres_lang', l);
    document.documentElement.lang = l;
    trackEvent('language_change', { language: l });
    const parts = location.pathname.split('/').filter(Boolean);
    if (LANGUAGES.includes(parts[0])) parts[0] = l;
    else parts.unshift(l);
    navigate(`/${parts.join('/')}${parts.length ? '/' : ''}`);
  }, [location.pathname, navigate]);

  useEffect(() => {
    const currentPathLanguage = location.pathname.split('/').filter(Boolean)[0];
    const nextLanguage = LANGUAGES.includes(currentPathLanguage) ? currentPathLanguage : lang;
    if (nextLanguage !== lang) setLangState(nextLanguage);
    document.documentElement.lang = nextLanguage;
    localStorage.setItem('tres_lang', nextLanguage);
  }, [lang, location.pathname]);

  const t = useCallback((key) => {
    // Check alias
    const realKey = KEY_ALIASES[key] || key;
    // Check static value (numbers like '11', '1200', '∞')
    if (KEY_ALIASES[key] && !TRANSLATIONS.de[realKey]) return KEY_ALIASES[key];
    // Check extra translations
    const extra = EXTRA[lang]?.[key] || EXTRA.de?.[key];
    if (extra && !TRANSLATIONS[lang]?.[realKey]) return extra;
    // Main translations
    return TRANSLATIONS[lang]?.[realKey] || TRANSLATIONS.de[realKey] || extra || key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};
